import React, { useState } from 'react';
import { 
    MessageSquare, ListChecks, BarChart3, Download, 
    FileText, User, Calendar, ChevronDown, ChevronUp 
} from 'lucide-react';
import TranscriptView from './TranscriptView';
import QAView from './QAView';
import AggregatedView from './AggregatedView';
import api from '../../../utils/api';
import toast from 'react-hot-toast';

const EnhancedResponseViewer = ({ surveyId, instances, survey }) => {
    const [activeTab, setActiveTab] = useState('transcript');
    const [exporting, setExporting] = useState(false);

    const tabs = [
        { 
            id: 'transcript', 
            label: 'Transcript View', 
            icon: MessageSquare,
            description: 'Full conversational flow'
        },
        { 
            id: 'qa', 
            label: 'Q&A View', 
            icon: ListChecks,
            description: 'Structured question-answer format'
        },
        { 
            id: 'aggregated', 
            label: 'Aggregated View', 
            icon: BarChart3,
            description: 'Responses grouped by question'
        }
    ];

    const handleExportAll = async (format = 'csv') => {
        try {
            setExporting(true);
            const response = await api.get(`/surveys/${surveyId}/export`, { 
                responseType: 'blob',
                params: { format }
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `survey_${surveyId}_all_responses.${format}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success(`Export downloaded as ${format.toUpperCase()}!`);
        } catch (error) {
            console.error("Export failed", error);
            toast.error("Export failed");
        } finally {
            setExporting(false);
        }
    };

    const completedInstances = instances.filter(i => i.status === 'completed');
    const completionRate = instances.length > 0 
        ? Math.round((completedInstances.length / instances.length) * 100) 
        : 0;

    return (
        <div className="space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-blue-700">Total Responses</p>
                            <p className="text-2xl font-bold text-blue-900 mt-1">{instances.length}</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                            <User className="w-6 h-6 text-white" />
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-green-700">Completed</p>
                            <p className="text-2xl font-bold text-green-900 mt-1">{completedInstances.length}</p>
                        </div>
                        <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                            <FileText className="w-6 h-6 text-white" />
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-blue-700">Completion Rate</p>
                            <p className="text-2xl font-bold text-blue-900 mt-1">{completionRate}%</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                            <BarChart3 className="w-6 h-6 text-white" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="border-b border-gray-200 bg-gray-50">
                    <div className="flex overflow-x-auto scrollbar-hide">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex-1 min-w-[200px] px-6 py-4 text-left border-b-2 transition-all ${
                                        activeTab === tab.id
                                            ? 'border-blue-600 bg-white'
                                            : 'border-transparent hover:bg-gray-100'
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                            activeTab === tab.id 
                                                ? 'bg-blue-100' 
                                                : 'bg-gray-100'
                                        }`}>
                                            <Icon 
                                                size={20} 
                                                className={activeTab === tab.id ? 'text-blue-600' : 'text-gray-500'} 
                                            />
                                        </div>
                                        <div>
                                            <p className={`font-semibold text-sm ${
                                                activeTab === tab.id ? 'text-blue-600' : 'text-gray-700'
                                            }`}>
                                                {tab.label}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-0.5">{tab.description}</p>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Export Actions */}
                <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                    <p className="text-sm text-gray-600">
                        {activeTab === 'transcript' && 'View full conversational transcripts'}
                        {activeTab === 'qa' && 'Structured question-answer format per employee'}
                        {activeTab === 'aggregated' && 'All responses grouped by question'}
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => handleExportAll('csv')}
                            disabled={exporting || completedInstances.length === 0}
                            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Download size={14} />
                            {exporting ? 'Exporting...' : 'Export CSV'}
                        </button>
                        <button
                            onClick={() => handleExportAll('pdf')}
                            disabled={exporting || completedInstances.length === 0}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Download size={14} />
                            {exporting ? 'Exporting...' : 'Export PDF'}
                        </button>
                    </div>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                    {activeTab === 'transcript' && (
                        <TranscriptView instances={completedInstances} />
                    )}
                    {activeTab === 'qa' && (
                        <QAView instances={completedInstances} survey={survey} />
                    )}
                    {activeTab === 'aggregated' && (
                        <AggregatedView instances={completedInstances} survey={survey} surveyId={surveyId} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default EnhancedResponseViewer;
